/**
 * WASSER E-commerce Platform - Shared Color System
 *
 * Унифицированные цвета для Client и Admin приложений.
 * Используйте эти токены вместо хардкода hex значений.
 *
 * @example
 * import { statusColors, brandColors } from '@shared/theme/colors';
 *
 * // В компонентах
 * <Box bgcolor={statusColors.success.light}>...</Box>
 */

/**
 * Создаёт alpha-версию цвета
 * @param {string} hex - HEX цвет
 * @param {number} alphaValue - Прозрачность (0-1)
 * @returns {string} - rgba цвет
 */
export const alpha = (hex, alphaValue) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alphaValue})`;
};

/**
 * Status Colors - для индикаторов состояния
 * Используются для: наличие товара, статусы заказов, уведомления
 */
export const statusColors = {
  success: {
    main: '#4caf50',
    light: 'rgba(76, 175, 80, 0.1)',
    dark: '#388e3c',
    text: '#2e7d32',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ff9800',
    light: 'rgba(255, 152, 0, 0.1)',
    dark: '#f57c00',
    text: '#e65100',
    contrastText: '#ffffff',
  },
  error: {
    main: '#f44336',
    light: 'rgba(244, 67, 54, 0.1)',
    dark: '#d32f2f',
    text: '#c62828',
    contrastText: '#ffffff',
  },
  info: {
    main: '#2196f3',
    light: 'rgba(33, 150, 243, 0.1)',
    dark: '#1976d2',
    text: '#1565c0',
    contrastText: '#ffffff',
  },
};

/**
 * Brand Colors - ЕДИНЫЕ основные цвета бренда для Client и Admin
 *
 * 🔴 ВАЖНО: Используйте ОДИНАКОВЫЕ цвета во всех приложениях
 * для обеспечения визуальной согласованности бренда WASSER.
 */
export const brandColors = {
  primary: {
    main: '#2C3E50', // Темный сине-серый (профессиональный)
    light: '#34495E',
    dark: '#1A252F',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#E74C3C', // Красный (акцент)
    light: '#EC7063',
    dark: '#C0392B',
    contrastText: '#ffffff',
  },
  // Дополнительные акцентные цвета для расширенной палитры
  accent: {
    blue: '#3498DB', // Информационный синий
    green: '#27AE60', // Успех/подтверждение
    orange: '#F39C12', // Предупреждение
    purple: '#9B59B6', // Премиум/особые
  },
};

/**
 * Background Colors - фоновые цвета
 */
export const backgroundColors = {
  default: '#F8F9FA',
  paper: '#FFFFFF',
  dark: '#1A252F',
  hover: 'rgba(0, 0, 0, 0.04)',
  selected: 'rgba(0, 0, 0, 0.08)',
  disabled: 'rgba(0, 0, 0, 0.12)',
};

/**
 * Text Colors - цвета текста
 */
export const textColors = {
  primary: '#2C3E50',
  secondary: '#7F8C8D',
  disabled: 'rgba(0, 0, 0, 0.38)',
  hint: 'rgba(0, 0, 0, 0.38)',
};

/**
 * Border Colors - цвета границ
 */
export const borderColors = {
  default: 'rgba(0, 0, 0, 0.12)',
  light: 'rgba(0, 0, 0, 0.06)',
  dark: 'rgba(0, 0, 0, 0.23)',
  focus: '#1976d2',
};

/**
 * Shadow Colors - для теней
 */
export const shadowColors = {
  light: 'rgba(0, 0, 0, 0.08)',
  medium: 'rgba(0, 0, 0, 0.12)',
  dark: 'rgba(0, 0, 0, 0.16)',
};

/**
 * Stock Status Colors - специально для отображения наличия товара
 * Используется в ProductCard, CartItem, и т.д.
 */
export const stockColors = {
  inStock: {
    // >10 шт
    chip: statusColors.success.main,
    background: statusColors.success.light,
    text: statusColors.success.text,
  },
  lowStock: {
    // 1-10 шт
    chip: statusColors.warning.main,
    background: statusColors.warning.light,
    text: statusColors.warning.text,
  },
  outOfStock: {
    // 0 шт
    chip: statusColors.error.main,
    background: statusColors.error.light,
    text: statusColors.error.text,
  },
};

/**
 * Order Status Colors - для статусов заказов
 */
export const orderStatusColors = {
  pending: {
    main: '#ff9800',
    light: 'rgba(255, 152, 0, 0.1)',
    text: '#e65100',
  },
  confirmed: {
    main: '#2196f3',
    light: 'rgba(33, 150, 243, 0.1)',
    text: '#1565c0',
  },
  processing: {
    main: '#9c27b0',
    light: 'rgba(156, 39, 176, 0.1)',
    text: '#7b1fa2',
  },
  shipped: {
    main: '#00bcd4',
    light: 'rgba(0, 188, 212, 0.1)',
    text: '#00838f',
  },
  delivered: {
    main: '#4caf50',
    light: 'rgba(76, 175, 80, 0.1)',
    text: '#2e7d32',
  },
  cancelled: {
    main: '#f44336',
    light: 'rgba(244, 67, 54, 0.1)',
    text: '#c62828',
  },
};

export default {
  alpha,
  statusColors,
  brandColors,
  backgroundColors,
  textColors,
  borderColors,
  shadowColors,
  stockColors,
  orderStatusColors,
};
